import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Svg, { Circle, Path } from 'react-native-svg';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { MatchReferee } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/**
 * An ISO-3166 alpha-2 country code → flag emoji (regional indicator pair). Only
 * applied for a clean two-letter alpha code; anything else yields '' so we never
 * render mojibake next to the referee's country name.
 */
function flagFromCountryCode(code: string | null): string {
  if (!code || !/^[a-zA-Z]{2}$/.test(code)) return '';
  const upper = code.toUpperCase();
  const base = 0x1f1e6; // regional indicator 'A'
  return String.fromCodePoint(
    base + (upper.charCodeAt(0) - 65),
    base + (upper.charCodeAt(1) - 65),
  );
}

/** A small whistle glyph (Ionicons has none), drawn so it reads as an official. */
function WhistleIcon({ color }: { color: string }) {
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24">
      {/* body + spout */}
      <Path
        d="M3 10.5h9.5a2 2 0 0 1 2 2v1.2a4.8 4.8 0 1 1-9.6 0V14H3a1.5 1.5 0 0 1-1.5-1.5v-0.5A1.5 1.5 0 0 1 3 10.5z"
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      {/* mouthpiece */}
      <Path d="M14.5 11l3.5-2.2" stroke={color} strokeWidth={1.6} strokeLinecap="round" />
      {/* pea */}
      <Circle cx={9.7} cy={14.8} r={1.5} fill={color} />
    </Svg>
  );
}

/** One career stat: a value over a small label. */
function CareerStat({ value, label, tint }: { value: string; label: string; tint?: string }) {
  return (
    <View className="items-center gap-0.5">
      <AppText variant="caption" weight="bold" tabular style={tint ? { color: tint } : undefined} className={tint ? '' : 'text-ink'}>
        {value}
      </AppText>
      <AppText variant="micro" weight="medium" className="text-muted">
        {label}
      </AppText>
    </View>
  );
}

/**
 * A small referee card: a whistle glyph + the official's name and country, with a
 * mini career row (games, yellows-per-game average, total reds). Placed near the
 * full-stats block. The caller hides it entirely when `referee` is null; the
 * career row itself is omitted when the provider gave no career numbers.
 */
export function RefereeCard({ referee }: { referee: MatchReferee }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();

  const flag = flagFromCountryCode(referee.country_code);
  const country = referee.country
    ? `${flag ? `${flag} ` : ''}${referee.country}`
    : flag;

  const career = referee.career;
  // Yellows per game (one decimal), guarded against a zero-game record.
  const yellowAvg =
    career && career.games > 0
      ? (Math.round((career.yellow_cards / career.games) * 10) / 10).toFixed(1)
      : null;

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-3 px-4 py-3">
        <View
          className="h-10 w-10 items-center justify-center rounded-full bg-volt-glass"
          style={{ borderWidth: 1, borderColor: palette.voltLine }}
        >
          <WhistleIcon color={palette.voltBright} />
        </View>
        <View className="flex-1">
          <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
            {t('match.referee')}
          </AppText>
          <AppText variant="body" weight="bold" className="text-ink" numberOfLines={1}>
            {referee.name}
          </AppText>
          {country ? (
            <AppText variant="micro" className="text-soft" numberOfLines={1}>
              {country}
            </AppText>
          ) : null}
        </View>
      </View>

      {career ? (
        <View className="flex-row items-center justify-around border-t border-line px-4 py-3">
          <CareerStat
            value={localizeDigits(String(career.games), arabicNumerals)}
            label={t('match.refGames')}
          />
          <CareerStat
            value={localizeDigits(yellowAvg ?? '0', arabicNumerals)}
            label={t('match.refYellowAvg')}
            tint={palette.captain}
          />
          <CareerStat
            value={localizeDigits(String(career.red_cards), arabicNumerals)}
            label={t('match.refReds')}
            tint={palette.loss}
          />
        </View>
      ) : null}
    </Card>
  );
}
